import moment from 'moment';
import { Spinner } from '@fluentui/react-components';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { SettlementDebt } from "../../../models/Entities/SettlementDebt/SettlementDebt";
import { isDebtPaid } from './SettlementDebtsDetails.Summary';
import { formatRate } from '../../../commons/fee/PractitionerFee.Logic';

interface SettlementDebtsInfoProps {
    SettlementDebts: SettlementDebt[];
    /** Registrar el cobro (o pago) de una deuda pendiente; sin esto no hay botón. */
    onSettle?: (debt: SettlementDebt) => void;
    /** Texto del botón según el sentido: "Registrar cobro" o "Registrar pago". */
    settleLabel?: string;
}

const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency);

/** Una fila por deuda: fecha, importe, estado y saldo. Sin detalle todavía, la fila espera. */
const SettlementDebtsInfoComponent = ({ SettlementDebts, onSettle, settleLabel }: SettlementDebtsInfoProps) => {
    if (!SettlementDebts || SettlementDebts.length === 0) return null;

    return (
        <ul className="sdd-list">
            {SettlementDebts.map((settlementDebt) => {
                const debt = settlementDebt.CashFlowDebt;
                const key = `${settlementDebt.id}-${settlementDebt.debtId}`;

                if (!debt) {
                    return (
                        <li key={key} className="sdd-row sdd-row--loading">
                            <span className="sdd-row__dot" aria-hidden />
                            <span className="sdd-row__ref">Deuda #{settlementDebt.debtId}</span>
                            <span className="sdd-row__loading"><Spinner size="extra-tiny" /> Cargando detalle…</span>
                        </li>
                    );
                }

                const paid = isDebtPaid(settlementDebt);
                const currency = debt.currencyCode?.toUpperCase() || 'ARS';
                const originalCurrency = debt.originalCurrencyCode?.toUpperCase();
                const hasConversion = !!originalCurrency && originalCurrency !== currency && debt.originalAmountInOriginalCurrency != null;

                return (
                    <li key={key} className={`sdd-row${paid ? ' sdd-row--paid' : ' sdd-row--pending'}`}>
                        <span className="sdd-row__dot" aria-hidden />
                        <span className="sdd-row__ref">
                            <span className="sdd-row__id">#{debt.id}</span>
                            {debt.debtDate && <span className="sdd-row__date">{moment(debt.debtDate).format('DD/MM/YYYY')}</span>}
                        </span>
                        <span className="sdd-row__amount">
                            <span className="sdd-row__amount-main">{fmt(debt.originalAmount, currency)}</span>
                            {/* Deuda registrada en otra moneda al confirmar: importe original × cotización usada */}
                            {hasConversion && (
                                <span
                                    className="sdd-row__amount-original"
                                    title={`Importe en la moneda original${debt.currencyExchangeRate > 0 ? `, convertido a ${formatRate(debt.currencyExchangeRate)}` : ''}`}
                                >
                                    {fmt(debt.originalAmountInOriginalCurrency, originalCurrency!)}
                                    {debt.currencyExchangeRate > 0 && ` × ${formatRate(debt.currencyExchangeRate)}`}
                                </span>
                            )}
                        </span>
                        <span className={`sdd-row__badge${paid ? ' sdd-row__badge--paid' : ' sdd-row__badge--pending'}`}>
                            {paid ? 'Pagada' : 'Pendiente'}
                        </span>
                        <span className="sdd-row__pending">
                            {paid ? <span className="sdd-row__zero">—</span> : fmt(debt.pendingAmount, currency)}
                        </span>
                        {!paid && onSettle && (
                            <button type="button" className="sdd-row__settle" onClick={() => onSettle(settlementDebt)}>
                                {settleLabel ?? 'Registrar cobro'}
                            </button>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default SettlementDebtsInfoComponent;
